import React, { type ReactElement, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { useMutation, useQuery, useQueryClient } from "react-query"
import { useParams } from "react-router-dom"

import * as Yup from "yup"
import CloseIcon from "@mui/icons-material/Close"
import PolylineIcon from "@mui/icons-material/Polyline"
import {
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  useMediaQuery,
  useTheme as useMuiTheme,
} from "@mui/material"
import { Button, InputText, NotificationUtils, Skeleton, Tab, Tabs, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import { FormikProvider, useFormik } from "formik"
import { v4 as uuidv4 } from "uuid"

import { InfoContainer } from "../../../components/core/InfoContainer"
import { getTheme } from "../../../hooks/UseTheme"
import { KonanAPI } from "../../../services/KonanAPI"
import type { ScriptGroupRetrieve } from "../../../types/generated/api/ScriptGroupRetrieve"
import type { ScriptSchema } from "../../../types/generated/api/ScriptSchema"
import type { ScriptSchemaFeature } from "../../../types/generated/api/ScriptSchemaFeature"
import type { ScriptTemplateRetrieve } from "../../../types/generated/api/ScriptTemplateRetrieve"
import type { ScriptTestOutput } from "../../../types/generated/api/ScriptTestOutput"
import { mapFeaturesListToKeyValuePair } from "../../../utils/deploymentDetailsHelpers"
import {
  type BuiltinScriptDialogProps,
  type CreateBuiltinScriptRequest,
  type Cred,
  type InitialFormState,
  ScriptTab,
  type ScriptTestFeature,
  type TestScriptRequest,
  type UpdateScriptRequest,
} from "../../Scripts/Interfaces"
import { ScriptLogs } from "../../Scripts/components/ScriptComponents"
import type { Features } from "../../Workflows/types/interfaces"
import { IntegrationSetup, IntegrationTesting } from "./MarketPlaceComponents"

import styles from "../MarketPlace.module.scss"

// Yup validation schema
const validationSchema = Yup.object({
  scriptName: Yup.string().required("Script name is required"),
})

// Mini Components
function LoadingTemplateInfo({ t }: { t: (key: string) => string }): ReactElement {
  return (
    <Grid item container xs minHeight={"80%"} mb={2}>
      <InfoContainer icon={<CircularProgress />} title={t("builtinScriptDialog.labels.loadingTemplateInfo")} />
    </Grid>
  )
}

function NotTestableMessage({ t }: { t: (key: string) => string }): ReactElement {
  return (
    <Grid
      container
      justifyContent={"center"}
      alignItems={"center"}
      style={{ backgroundColor: "var(--base-background-3)" }}
      flexDirection={"column"}
      borderRadius={1}
      border={1}
      borderColor={"var(--base-border-1)"}
    >
      <Typography variant="p">{t("notTestable.title")}</Typography>
      <Typography variant="p">{t("notTestable.description")}</Typography>
    </Grid>
  )
}

// Helper functions for data formatting
function formatResponseToCredsKeyValue(secrets: ScriptSchemaFeature[] | undefined): Cred[] | undefined {
  return secrets?.map((cred) => ({
    feat: cred?.name ?? "",
    value: "",
    type: cred?.type ?? "",
    isRequired: cred?.is_required ?? false,
  }))
}

function formatResponseToTestFeatures(
  inputFeatures: ScriptSchemaFeature[] | undefined,
): Record<string, ScriptTestFeature> | undefined {
  return inputFeatures?.reduce(
    (acc, feature) => {
      const id = uuidv4()

      return {
        ...acc,
        [id]: {
          id,
          value: "",
          original: true,
          ...feature,
        } as ScriptTestFeature,
      }
    },
    {} as Record<string, ScriptTestFeature>,
  )
}

/**
 * Represents a dialog component for managing built-in scripts. This includes creating new scripts,
 * editing existing ones, and viewing details. It handles various states and modes such as edit, create,
 * and read. The dialog integrates with various backend services to fetch and update scripts or create new ones.
 *
 * @param {boolean} isOpen Controls the visibility of the dialog.
 * @param {Function} onClose Callback function to execute when the dialog is closed.
 * @param {object} [scriptInfo] Contains optional details of the script like name and UUID.
 * @param {"edit" | "create" | "read"} [workflowMode] Specifies the mode of the dialog related to workflow operations.
 * @param {boolean} [isNewScriptTriggered] Indicates if a new script creation was initiated.
 * @param {number} [scriptsCount] Number of scripts, if needed for rendering or calculations.
 * @param {"workflow" | "scripts"} [renderView] Specifies the context in which the dialog is used.
 * @param {boolean} [openInEditMode] Determines if the script should open in edit mode directly.
 * @param {Array<SchemaFeature>} [workflowSchema] Schema definition for workflows, if applicable.
 * @param {FormikProps<any>} [workflowFormik] Formik props for managing form state in workflow contexts.
 *
 * @returns {React.ReactElement} A React element representing the built-in script dialog.
 */
export function MarketPlaceIntegrationDialog(props: Readonly<BuiltinScriptDialogProps>): React.ReactElement {
  const {
    isOpen,
    onClose,
    renderView,
    openInEditMode = false,
    isNewScriptTriggered = false,
    scriptInfo,
    workflowSchema,
    workflowFormik,
    tab,
    setFormikScriptData,
    canShowDetails,
  } = props

  //  hooks first
  const { id: projectId } = useParams<{ id: string }>()
  const theme = getTheme()
  const MuiTheme = useMuiTheme()
  const queryClient = useQueryClient()
  const { t } = useTranslation("scripts")

  // State Hooks
  const [selectedState, setSelectedState] = useState<ScriptTab>(tab ?? ScriptTab.ScriptSetup)
  const [toggleOpenInEditMode, setToggleOpenInEditMode] = useState<boolean>(openInEditMode)
  // state to handle the current mode
  const [mode, setMode] = useState<{ create: boolean; edit: boolean }>({
    create: !!canShowDetails && isNewScriptTriggered,
    edit: Boolean(openInEditMode),
  })
  const [isConsoleExpanded, setIsConsoleExpanded] = useState<boolean>(false)
  const [isLogsExpanded, setIsLogsExpanded] = useState<boolean>(false)

  // constants
  const isScriptInViewMode = !mode.create && !mode.edit

  // fetch data (useQuery)
  const { data: scriptData, isLoading: isScriptDataLoading } = useQuery<AxiosResponse<ScriptGroupRetrieve>, AxiosError>(
    ["script", projectId, scriptInfo?.scriptId],
    () =>
      KonanAPI.fetchScript({
        projectId: projectId as string,
        scriptId: scriptInfo?.scriptId as string,
      }),
    { enabled: canShowDetails && !!projectId && !!scriptInfo?.scriptId },
  )

  // Mutations
  const updateScriptMutation = useMutation<
    AxiosResponse<ScriptGroupRetrieve>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    AxiosError<any>,
    UpdateScriptRequest
  >(KonanAPI.updateScript, {
    onSuccess: async (res) => {
      NotificationUtils.toast(t("builtinScriptDialog.notifications.updateSuccess", { scriptName: res?.data?.name }), {
        severity: "success",
      })
      onClose(res?.data?.uuid)
      setTimeout(() => {
        setMode({ edit: false, create: false })
      }, 300)

      queryClient.invalidateQueries(["scripts", projectId])
    },

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (response: any) => {
      if (response?.response?.data?.details) {
        NotificationUtils.toast(response?.response?.data?.details?.slice(1, -1), {
          severity: "error",
        })
      } else if (response?.response?.data?.name) {
        NotificationUtils.toast(response?.response?.data?.name[0], {
          severity: "error",
        })
      } else if (response?.response?.data?.length > 0) {
        NotificationUtils.toast(response?.response?.data[0], {
          severity: "error",
        })
      } else {
        NotificationUtils.toast(t("builtinScriptDialog.notifications.updateErrorGeneric"), {
          severity: "error",
        })
      }
    },
  })

  // create builtin script
  const createBuiltinScriptMutation = useMutation<AxiosResponse, AxiosError, CreateBuiltinScriptRequest>(
    KonanAPI.createBuiltinScript,
    {
      onSuccess: async (res) => {
        await queryClient.invalidateQueries(["ready-templates", projectId])
        await queryClient.invalidateQueries(["scripts", projectId])
        NotificationUtils.toast(t("builtinScriptDialog.notifications.createSuccess"), {
          severity: "success",
        })
        onClose(res?.data?.uuid)
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onError(error: any) {
        if (error?.response?.data?.details) {
          NotificationUtils.toast(error?.response?.data?.details, {
            severity: "error",
          })
        } else if (error?.response?.data?.length > 0) {
          NotificationUtils.toast(error?.response?.data[0], {
            severity: "error",
          })
        } else if (error?.response?.data?.file?.length > 0) {
          NotificationUtils.toast(error?.response?.data?.file[0], {
            severity: "error",
          })
        } else {
          NotificationUtils.toast(t("builtinScriptDialog.notifications.createErrorGeneric"), {
            severity: "error",
          })
        }
      },
    },
  )

  const testScriptMutation = useMutation<AxiosResponse<ScriptTestOutput>, AxiosError, TestScriptRequest>(
    KonanAPI.testScript,
    {
      mutationKey: ["Test-script-mutation", scriptInfo?.uuid],
      onSuccess: async (res) => {
        NotificationUtils.toast(
          res?.data?.succeeded
            ? t("builtinScriptDialog.notifications.testScript.success")
            : t("builtinScriptDialog.notifications.testScript.scriptError"),
          {
            severity: res?.data?.succeeded ? "success" : "error",
          },
        )
      },

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onError: (response: any) => {
        if (response?.response?.data?.details) {
          NotificationUtils.toast(response?.response?.data?.details?.slice(1, -1), {
            severity: "error",
          })
        } else {
          NotificationUtils.toast(t("builtinScriptDialog.notifications.testScript.genericError"), {
            severity: "error",
          })
        }
      },
    },
  )

  // UseMemo
  // current selected script from the dropdown
  const selectedScript = useMemo(() => {
    // first check if script in the script listing
    if (scriptData) {
      return scriptData?.data
    }
    // check if script exists in scriptInfo in case for example, the retrieved script is actually deleted
    // so in this case we need the script info but the script itself won't exist in the scripts listing
    // so we extract its data from scriptInfo (retrieved script node data)
    else if (isScriptInViewMode && scriptInfo?.uuid && scriptInfo?.uuid) {
      return scriptInfo
    }
  }, [scriptData, isScriptInViewMode, scriptInfo])

  const extractedConsoleData = useMemo((): string[] => {
    if (testScriptMutation.isError) return [t("extractedConsoleData.failedToExecute")]
    else if (testScriptMutation.data?.data && !testScriptMutation.data?.data?.succeeded)
      return [t("extractedConsoleData.errorCheckLogs")]
    else if (testScriptMutation.data?.data?.succeeded && testScriptMutation.data?.data?.output)
      return [JSON.stringify(testScriptMutation.data?.data?.output)]
    else return [t("extractedConsoleData.runTestPrompt")]
  }, [testScriptMutation.data?.data, testScriptMutation.isError, t])

  // helper functions (formik, handle functs, ..ext)
  const initialFormikState: InitialFormState = {
    scriptName: scriptInfo?.name ?? selectedScript?.name ?? "",
    templateId: "",
    creds: [],
    testFeatures: {},
  }

  const formik = useFormik({
    validationSchema: validationSchema,
    initialValues: initialFormikState,
    onSubmit: async ({ scriptName, creds }) => {
      const filteredFeatures = mapFeaturesListToKeyValuePair(creds)

      if (isNewScriptTriggered) {
        const baseMutationParams = {
          project_uuid: projectId as string,
          template_uuid: scriptInfo.uuid,
          name: scriptName,
        }
        const mutationParams =
          creds?.length > 0 && Object.keys(filteredFeatures)?.length > 0
            ? { ...baseMutationParams, creds: filteredFeatures }
            : baseMutationParams

        await createBuiltinScriptMutation.mutateAsync(mutationParams)
      } else {
        const baseMutationParams = {
          name: formik.values.scriptName,
          project_uuid: projectId as string,
          script_uuid: selectedScript?.uuid as string,
        }

        const mutationParams =
          creds?.length > 0 && Object.keys(filteredFeatures)?.length > 0
            ? { ...baseMutationParams, creds: filteredFeatures }
            : baseMutationParams

        await updateScriptMutation.mutateAsync(mutationParams)
      }
    },
  })

  // fetch origin template
  const {
    data: templateData,
    isLoading: isTemplateDataLoading,
    isFetching: isTemplateDataFetching,
    isError: isFetchingTemplateDataError,
    refetch: refetchTemplate,
  } = useQuery<AxiosResponse<ScriptTemplateRetrieve>, AxiosError>(
    ["ready-template", projectId, selectedScript, scriptInfo],
    () =>
      KonanAPI.fetchReadyTemplate({
        projectId: projectId as string,
        templateId: scriptInfo.uuid ?? (selectedScript?.uuid as string),
      }),
    {
      onSuccess: (response) => {
        formik.setFieldValue("creds", formatResponseToCredsKeyValue(response?.data?.schema?.secrets) ?? [])
        formik.setFieldValue("testFeatures", formatResponseToTestFeatures(response?.data?.schema?.input_features) ?? {})

        setFormikScriptData?.(response.data.schema as ScriptSchema)
      },
      enabled:
        canShowDetails && !!projectId && (!!formik.values.templateId || !!selectedScript?.name || !!scriptInfo?.uuid),
    },
  )

  const handleRunTestClicked = async (): Promise<void> => {
    const features = Object.values(formik.values.testFeatures).reduce((acc, feature) => {
      return {
        ...acc,
        [feature.name]: feature.value,
      }
    }, {} as Features)

    testScriptMutation.mutateAsync({
      project_uuid: projectId as string,
      script_uuid: scriptInfo?.scriptId ?? "",
      features,
    })
  }

  const handleCancelClicked = (): void => {
    setMode({ edit: false, create: false })

    if (toggleOpenInEditMode) {
      formik.setFieldValue("scriptName", scriptInfo?.name)
      setToggleOpenInEditMode(false)
    } else {
      formik.setValues({
        ...formik.values,
        creds: formatResponseToCredsKeyValue(templateData?.data.schema?.secrets) ?? [],
        scriptName: scriptData?.data.name as string,
      })
    }
  }

  // Tab rendering helper functions
  const renderTestingTab = (): ReactElement => {
    return (
      <IntegrationTesting
        handleRunTestClicked={handleRunTestClicked}
        isLoading={testScriptMutation.isLoading}
        testingLogsProps={{
          logs: testScriptMutation.data?.data.logs,
          consoleLogs: extractedConsoleData,
          isTestScriptMutationLoading: testScriptMutation.isLoading,
          isConsoleExpanded: isConsoleExpanded,
          isLogsExpanded: isLogsExpanded,
          setIsLogsExpanded: setIsLogsExpanded,
          setIsConsoleExpanded: setIsConsoleExpanded,
        }}
      />
    )
  }

  const renderSetupTab = (): ReactElement => {
    return (
      <IntegrationSetup
        template={{
          templateData: templateData?.data,
          isTemplateDataLoading,
          isTemplateDataFetching,
          isFetchingTemplateDataError,
          refetchTemplate,
        }}
        script={{
          scriptData: scriptData?.data,
          isScriptDataLoading,
        }}
        isScriptInViewMode={isScriptInViewMode}
        workflowSchema={workflowSchema}
        workflowFormik={workflowFormik}
        formik={formik}
      />
    )
  }

  const renderLogsTab = (): ReactElement => {
    return <ScriptLogs uuid={scriptData?.data.uuid as string} />
  }

  const getComponentForTab = (tab: ScriptTab): ReactElement => {
    // Show loading state
    if (isTemplateDataLoading || isScriptDataLoading) {
      return <LoadingTemplateInfo t={t} />
    }

    // Handle restricted permissions (no retrieve access)
    if (!canShowDetails) {
      // User can test if script is already connected
      if (!isNewScriptTriggered) {
        return renderTestingTab()
      }
      // Script is not connected - cannot test
      return <NotTestableMessage t={t} />
    }

    // Render tabs based on selection (full access)
    switch (tab) {
      case ScriptTab.ScriptSetup:
        return renderSetupTab()

      case ScriptTab.Testing:
        return renderTestingTab()

      case ScriptTab.Logs:
        return renderLogsTab()

      default:
        return <></>
    }
  }

  const tabs = useMemo(
    () =>
      canShowDetails
        ? [ScriptTab.ScriptSetup, ...(isNewScriptTriggered ? [] : [ScriptTab.Testing, ScriptTab.Logs])]
        : [ScriptTab.Testing],
    [canShowDetails, isNewScriptTriggered],
  )

  return (
    <Dialog
      sx={{
        "& .MuiPaper-root": {
          md: { height: "max(60vh, 700px)" },
        },
      }}
      open={isOpen}
      fullWidth
      maxWidth={"md"}
      fullScreen={useMediaQuery(MuiTheme.breakpoints.down("md"))}
      onClose={() => {
        onClose()
      }}
    >
      <DialogTitle className="dialog-header-base" style={{ display: "flex", justifyContent: "space-between" }}>
        <Typography variant="h2-bold">{t("builtinScriptDialog.title")}</Typography>
        <IconButton
          onClick={() => {
            onClose()
          }}
          size="small"
          className={"close-icon-button"}
        >
          <CloseIcon style={{ color: theme.palette.base.text[2] }} />
        </IconButton>
      </DialogTitle>

      <DialogContent className={styles.scriptDialogContent}>
        <Grid container item xs={12} flexDirection={"column"} flexWrap={"nowrap"} height={"100%"}>
          <Grid container item p={2} pb={0} flexDirection={"column"} flexWrap={"nowrap"}>
            <Grid container item xs={12} justifyContent="space-between">
              {scriptInfo.logo ? (
                <img
                  src={scriptInfo.logo}
                  alt={scriptInfo.name}
                  style={{ borderRadius: "4px", height: "52px", width: "52px", marginRight: "8px" }}
                />
              ) : (
                <Grid item className={styles.scriptSideBar}>
                  <PolylineIcon sx={{ color: theme.palette.base.text[1] }} />
                </Grid>
              )}

              <Grid item xs container direction="column" gap={0.5}>
                <Grid item>
                  <Typography variant="label-bold" variantColor={2}>
                    {t("builtinScriptDialog.title")}
                  </Typography>
                </Grid>

                <Grid container item xs>
                  {isScriptInViewMode && !(selectedScript?.name || scriptInfo?.name) ? (
                    <Grid item>
                      <Skeleton width={"60%"} height={18} />
                    </Grid>
                  ) : isScriptInViewMode && (selectedScript?.name || scriptInfo?.name) ? (
                    <Typography variant="h3-bold">{selectedScript?.name ?? scriptInfo?.name}</Typography>
                  ) : (
                    <InputText
                      disabled={formik.isSubmitting}
                      type="text"
                      handleChange={formik.handleChange}
                      value={formik.values.scriptName}
                      id="scriptName"
                      hideDescription
                      widthCalculator
                      placeholder={t("builtinScriptDialog.labels.scriptNamePlaceholder")}
                      error={formik.touched.scriptName && Boolean(formik.errors.scriptName) && formik.errors.scriptName}
                    />
                  )}
                </Grid>
              </Grid>

              {/* Control buttons (edit/save/cancel) */}
              {canShowDetails && renderView !== "workflow" && (
                <Grid container item xs gap={1} height="fit-content" display={"flex"} justifyContent="flex-end">
                  {!isNewScriptTriggered &&
                    (isScriptInViewMode ? (
                      <Button
                        onClick={() => {
                          setMode({ create: false, edit: true })
                        }}
                        disabled={formik.isSubmitting || isTemplateDataLoading}
                        variant="secondary"
                      >
                        {t("builtinScriptDialog.buttons.edit")}
                      </Button>
                    ) : (
                      mode.edit && (
                        <Button
                          disabled={createBuiltinScriptMutation.isLoading || updateScriptMutation.isLoading}
                          onClick={handleCancelClicked}
                          variant="secondary"
                        >
                          {t("builtinScriptDialog.buttons.cancel")}
                        </Button>
                      )
                    ))}

                  {(mode.create || mode.edit) && (
                    <Button
                      disabled={
                        createBuiltinScriptMutation.isLoading ||
                        updateScriptMutation.isLoading ||
                        !formik.values.scriptName
                      }
                      onClick={formik.submitForm}
                      variant="primary"
                      isLoading={createBuiltinScriptMutation.isLoading || updateScriptMutation.isLoading}
                    >
                      {t("builtinScriptDialog.buttons.save")}
                    </Button>
                  )}
                </Grid>
              )}
            </Grid>

            {!isNewScriptTriggered && canShowDetails && (
              <Grid container item xs={12} mt={1} className={styles.tabsBottomBorder}>
                <Tabs value={selectedState} className={styles.tabs}>
                  {tabs.map((label) => (
                    <Tab
                      key={label}
                      label={t(`types.ScriptTab.${label}`)}
                      value={label}
                      selected={label === selectedState}
                      onClick={() => setSelectedState(label)}
                      size="large"
                    />
                  ))}
                </Tabs>
              </Grid>
            )}
          </Grid>

          {/* Template info section (input features, output features, description, etc..) */}
          <Grid container item xs className={styles.scriptDialogTabContent}>
            <FormikProvider value={formik}>{getComponentForTab(selectedState)}</FormikProvider>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  )
}
